import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const AuthContext = createContext(null);
const API_BASE_URL = process.env.REACT_APP_API_URL;

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const codeExchangedRef = useRef(false);

  useEffect(() => {
    checkAuth();
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    
    console.log('Auth callback params:', { code, error });

    if (error) {
      console.error('OAuth error:', error);
      toast.error(`Authentication failed: ${error}`);
      return;
    }

    if (code && !codeExchangedRef.current) {
      console.log('Received OAuth code, starting exchange');
      codeExchangedRef.current = true;
      handleOAuthCallback(code);
    }
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (token) {
        const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
        if (!cognitoDomain) {
          throw new Error('Missing Cognito configuration');
        }

        const userInfoResponse = await axios.get(`${cognitoDomain}/oauth2/userInfo`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setUser(userInfoResponse.data);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthCallback = async (code) => {
    try {
      console.log('Starting OAuth callback with code:', code);
      
      // Exchange code for tokens using Cognito
      const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
      const clientId = process.env.REACT_APP_CLIENT_ID;
      const redirectUri = window.location.origin + '/';

      if (!cognitoDomain || !clientId) {
        throw new Error('Missing Cognito configuration');
      }

      const tokenResponse = await axios.post(`${cognitoDomain}/oauth2/token`, 
        new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: clientId,
          code: code,
          redirect_uri: redirectUri
        }).toString(),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        }
      );

      console.log('Token response:', tokenResponse.data);

      const { access_token, id_token, refresh_token } = tokenResponse.data;

      if (!access_token) {
        throw new Error('No access token received');
      }

      // Store tokens
      localStorage.setItem('accessToken', access_token);
      if (id_token) localStorage.setItem('idToken', id_token);
      if (refresh_token) localStorage.setItem('refreshToken', refresh_token);

      // Verify the token with Cognito
      const userInfoResponse = await axios.get(`${cognitoDomain}/oauth2/userInfo`, {
        headers: {
          'Authorization': `Bearer ${access_token}`
        }
      });

      console.log('User info response:', userInfoResponse.data);
      setUser(userInfoResponse.data);

      toast.success('Login successful!');
      window.history.replaceState({}, document.title, window.location.pathname);
      window.location.replace('/');
    } catch (error) {
      console.error('OAuth callback failed:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error(`Login failed: ${error.message || 'Please try again.'}`);
      // Don't redirect on error, let the user see the error message
    }
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    toast.success('Logged out successfully');
  };

  const value = {
    user,
    loading,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 